import {
  borderRadius,
  iconSize,
  semanticColors,
  spacing,
  typography
} from '@moderneinc/design-system-tokens';
import { ButtonBase, Collapse, Stack } from '@mui/material';
import { type FunctionComponent, type ReactNode, useId, useState } from 'react';
import { lineHeight } from '../../../constants/typography';
import { ShowLessIcon, ShowMoreIcon } from '../../../icons/icons';

type OverflowMenuSectionProps = {
  heading: string;
  defaultExpanded?: boolean;
  children: ReactNode;
  'data-testid'?: string;
};

export const OverflowMenuSection: FunctionComponent<
  OverflowMenuSectionProps
> = ({
  heading,
  defaultExpanded = false,
  children,
  'data-testid': dataTestId
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const contentId = useId();

  return (
    <Stack>
      <ButtonBase
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        data-testid={dataTestId}
        disableRipple
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          paddingTop: `${spacing.spacing_1}px`,
          paddingBottom: `${spacing.spacing_1}px`,
          paddingLeft: `${spacing.spacing_1_1_2}px`,
          paddingRight: `${spacing.spacing_1_1_2}px`,
          borderRadius: `${borderRadius.s}px`,
          textAlign: 'left',
          fontSize: typography.fontSize.caption,
          lineHeight: `${lineHeight.m}px`,
          fontWeight: typography.fontWeight.medium,
          color: semanticColors.color.text.primary,
          '&:hover': {
            backgroundColor: semanticColors.color.surface.rowHover
          }
        }}
      >
        <span>{heading}</span>
        {isExpanded ? (
          <ShowLessIcon size={iconSize.md} />
        ) : (
          <ShowMoreIcon size={iconSize.md} />
        )}
      </ButtonBase>
      <Collapse in={isExpanded} unmountOnExit>
        <Stack id={contentId} role="region" aria-label={heading}>
          {children}
        </Stack>
      </Collapse>
    </Stack>
  );
};
